import React, { useMemo, useRef } from "react";
import Chart from "react-apexcharts";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { Tooltip } from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { getStoreColor } from "../../constants/plasticColors";
import { storeLabel } from "../../utils/storeLabel";
import {
  formatCount,
  IReportBuckets,
  spanLabel,
} from "../../pages/ParcelReport/parcelReportData";
import type { IParcelReportStoreIndex } from "../../store/useParcelReport";

const csvCell = (value: string | number) => {
  let text = String(value ?? "");
  // A store name starting with = + - or @ would run as a formula in Excel.
  if (typeof value === "string" && /^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const stampCount = (n: number) => `${formatCount(n)} ${n === 1 ? "stamp" : "stamps"}`;

/** A store's share of the period's stamps over its expected share; null
 *  without stamps. Rounded like the backend's weekly index. */
const indexOf = (stamps: number, total: number, expected: number) =>
  total > 0 && expected > 0
    ? Math.round((stamps / total / expected) * 10000) / 10000
    : null;

/** Each value plus every value before it. */
const runningTotals = (values: number[]) => {
  let total = 0;
  return values.map((n) => (total += n));
};

interface ParcelReportIndexChartProps {
  storeIndex: IParcelReportStoreIndex;
  /** The report's days, which the index's day arrays follow. */
  days: string[];
  /** The page's day, week or month buckets, shared with the other charts. */
  buckets: IReportBuckets;
  bucketHeading: string;
  /** "All carriers" or the carrier's name, for the empty text. */
  carrierName: string;
  /** The store picked in the filters; "" for all stores. */
  locationId: string;
  /** Each period counts every stamp from the first day of the range up to
   *  its end, not only its own. */
  cumulative: boolean;
  /** Shared by the CSV and the image, without the extension. */
  fileName: string;
  /** Shown before the download buttons. */
  controls?: React.ReactNode;
}

/**
 * The store index, one line per store: the store's share of a period's
 * stamps over its usual share of the parcels. Grouped by day, week or month,
 * like the charts above it, per period or cumulative.
 */
export const ParcelReportIndexChart = React.memo(
  ({
    storeIndex,
    days,
    buckets,
    bucketHeading,
    carrierName,
    locationId,
    cumulative,
    fileName,
    controls,
  }: ParcelReportIndexChartProps) => {
    const snapshotRef = useRef(null);
    const [, takeScreenshot] = useScreenshot();
    const periods = buckets.buckets;

    // The report's days summed into the page's buckets: every store's stamps,
    // and the totals the index divides by. Always over every store.
    const sums = useMemo(() => {
      const stamps = periods.map(() => 0);
      const unlisted = periods.map(() => 0);
      const byStore = new Map<string, number[]>(
        storeIndex.stores.map((s) => [s.locationId, periods.map(() => 0)])
      );
      days.forEach((day, i) => {
        const b = buckets.bucketOfDay.get(day);
        if (b === undefined) return;
        stamps[b] += storeIndex.dayStamps?.[i] ?? 0;
        unlisted[b] += storeIndex.dayUnlistedStamps?.[i] ?? 0;
        for (const s of storeIndex.stores) {
          byStore.get(s.locationId)![b] += s.dayStamps?.[i] ?? 0;
        }
      });
      return { stamps, unlisted, byStore };
    }, [storeIndex, days, buckets, periods]);

    // One store picked: only its line, still measured against every store.
    const stores = useMemo(
      () =>
        locationId
          ? storeIndex.stores.filter((s) => s.locationId === locationId)
          : storeIndex.stores,
      [storeIndex, locationId]
    );
    const labels = useMemo(
      () => stores.map((s) => storeLabel({ name: s.name, cost_place: s.costPlace })),
      [stores]
    );
    // Cumulative: a period's stamps plus all those before it in the range.
    const totals = useMemo(
      () => (cumulative ? runningTotals(sums.stamps) : sums.stamps),
      [cumulative, sums]
    );
    const lines = useMemo(
      () =>
        stores.map((s) => {
          const perPeriod = sums.byStore.get(s.locationId) ?? [];
          const stamps = cumulative ? runningTotals(perPeriod) : perPeriod;
          return {
            stamps,
            index: stamps.map((n, b) => indexOf(n, totals[b], s.expected)),
          };
        }),
      [stores, sums, totals, cumulative]
    );
    const hasData = sums.stamps.some((n) => n > 0);
    const unlisted = sums.unlisted.reduce((sum, n) => sum + n, 0);

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = `${fileName}.png`;
        link.click();
      });
    };

    const downloadCsv = () => {
      const csvLines = [
        [
          "From",
          "To",
          "Stamps",
          ...labels.flatMap((label) => [`${label} index`, `${label} stamps`]),
        ],
        ...periods.map((p, b) => [
          // A cumulative row covers the range from its first day.
          cumulative ? periods[0].first : p.first,
          p.last,
          totals[b],
          ...lines.flatMap((l) => [l.index[b] ?? "", l.stamps[b] ?? 0]),
        ]),
      ].map((cells) => cells.map(csvCell).join(","));
      const blob = new Blob(["﻿", csvLines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    };

    const chartOptions = useMemo(
      () => ({
        chart: {
          type: "line" as const,
          zoom: { enabled: false },
          toolbar: { show: false },
        },
        stroke: { width: 3, curve: "straight" as const },
        markers: { size: 4, strokeWidth: 0, hover: { size: 6 } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: periods.map((p) => p.label),
          title: {
            text: bucketHeading,
            style: { fontSize: "14px", fontWeight: "bold" },
          },
          labels: { style: { fontSize: "12px" }, maxWidth: 120 },
          // The tooltip above already names the period.
          tooltip: { enabled: false },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          title: {
            text: "Index",
            style: { fontSize: "14px", fontWeight: "bold" },
          },
          labels: {
            minWidth: 24,
            formatter: (val: number) => (val == null ? "" : val.toFixed(1)),
          },
        },
        annotations: {
          yaxis: [
            {
              y: 1,
              borderColor: "#6b7280",
              strokeDashArray: 6,
              label: {
                text: "1.0 = in line with its size",
                position: "left",
                textAnchor: "start",
                style: {
                  color: "#374151",
                  background: "#f3f4f6",
                  fontSize: "11px",
                },
              },
            },
          ],
        },
        grid: { strokeDashArray: 5 },
        legend: {
          show: true,
          showForSingleSeries: true,
          position: "right" as const,
          fontSize: "14px",
          fontFamily: "Satoshi, sans-serif",
          labels: { colors: "#374151" },
          markers: { width: 12, height: 12, strokeWidth: 0, radius: 6 },
          itemMargin: { horizontal: 0, vertical: 4 },
        },
        colors: stores.map((s) => getStoreColor(s.costPlace)),
        responsive: [
          {
            breakpoint: 1024,
            options: {
              legend: {
                position: "bottom" as const,
                itemMargin: { horizontal: 8 },
              },
            },
          },
          {
            breakpoint: 640,
            options: { xaxis: { labels: { style: { fontSize: "10px" } } } },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          x: {
            // The days the period covers and the stamps in it, so a partial
            // first or last period, or one with few stamps, reads as one.
            // A cumulative point covers the range from its first day.
            formatter: (val: number, opts?: any) => {
              const b = opts?.dataPointIndex;
              const p = periods[b];
              if (!p) return String(val);
              const covered = cumulative
                ? spanLabel(periods[0].first, p.last)
                : p.title;
              return `${covered}, ${stampCount(totals[b] ?? 0)}`;
            },
          },
          y: {
            formatter: (val: number | null, opts?: any) => {
              if (val == null) {
                return cumulative
                  ? "no stamps yet"
                  : "no stamps in this period";
              }
              const store = stores[opts?.seriesIndex];
              const stamps = lines[opts?.seriesIndex]?.stamps[opts?.dataPointIndex] ?? 0;
              return `${val.toFixed(2)} (${stampCount(stamps)}, usual share ${
                store?.share ?? 0
              }%)`;
            },
          },
        },
      }),
      [periods, bucketHeading, stores, lines, totals, cumulative]
    );

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row sm:flex-wrap w-full items-start sm:items-center justify-end gap-2 sm:gap-3">
          {controls}
          {hasData && stores.length > 0 && (
            <>
              <button
                type="button"
                onClick={downloadCsv}
                className="bg-droppie-oranje border border-gray-border h-8 px-3 sm:px-6 py-0 rounded active:bg-droppie-zalm disabled:bg-sky-light transition ease-linear flex items-center justify-center gap-2"
              >
                <p className="font-satoshi font-medium text-white text-md sm:text-md">
                  Download CSV
                </p>
              </button>
              <PrimaryButton
                size="sm"
                onClick={downloadScreenshot}
                label="Download image"
                className="!bg-droppie-oranje !border-droppie-oranje"
              />
            </>
          )}
        </div>

        <div ref={snapshotRef} className="mt-4">
          <div className="flex items-center mb-2 sm:mb-0">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              {cumulative ? "Store index, cumulative" : "Store index"}
            </p>
            <Tooltip
              content="Each store's share of the period's stamps, divided by its usual share of the parcels. 1.0 means the store did as its size predicts, above 1 better, below 1 worse. Per day, week or month, like the charts above. Cumulative counts every stamp from the first day of the range up to that point, so the last point covers the whole range."
              placeholder={undefined}
            >
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>

          <div className="mt-4 h-[450px]">
            {!stores.length ? (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-gray-500 font-satoshi">
                  This store has no usual share of the parcels, so it has no
                  index.
                </p>
              </div>
            ) : hasData ? (
              <Chart
                // react-apexcharts compares options as JSON, which drops the
                // tooltip formatters; remount when the view, periods or
                // stores change.
                key={`${cumulative ? "cumulative" : "period"}|${periods
                  .map((p) => p.key)
                  .join(",")}|${stores.map((s) => s.locationId).join(",")}`}
                options={chartOptions}
                series={stores.map((s, i) => ({ name: labels[i], data: lines[i].index }))}
                type="line"
                height={450}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-gray-500 font-satoshi text-center px-4">
                  {carrierName === "All carriers"
                    ? "No stamps in this period, so there is no index."
                    : `No ${carrierName} stamps in this period, so there is no index.`}
                </p>
              </div>
            )}
          </div>
          {unlisted > 0 && (
            <p className="font-satoshi text-sm text-gray-text mt-2">
              {stampCount(unlisted)} at stores without a usual share are not in
              the index.
            </p>
          )}
        </div>
      </div>
    );
  }
);
